import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import i18n from "@/i18n";
import { lokaleAccountsKey } from "@/features/lokale-accounts/useLokaleAccounts";

export const duplicatenKey = ["beheer", "koppelingen", "duplicaten"] as const;

/**
 * Disabled below two characters, not debounced to nothing: a proposed match would be computed
 * from the data ADR-0018 declined to trust. `toonIedereen` turns on both listing everybody and
 * matching an e-mailadres, for a screen picking a persoon rather than identifying one.
 */
export function useZoekPersonen(
  term: string,
  toonIedereen = false,
  page?: number,
  pageSize?: number,
  /** Bevoegd voor **any** of these, never all - see the endpoint's own note. */
  functieIds?: string[],
  /** Narrows on beschikbaarheid, which is why both this and `reedsIngeschreven` name a window. */
  inschrijfperiodeId?: string,
  reedsIngeschreven?: string,
  /** The ondersteuners screen's own: off everywhere else a persoon without one has to stay findable. */
  alleenOndersteuners = false,
) {
  return useQuery({
    // toonIedereen is in the key as well as in the request: the two callers ask the same route
    // two different questions, and one term would otherwise serve the other's cached answer.
    queryKey: [
      "beheer",
      "koppelingen",
      "personen",
      term,
      toonIedereen,
      page,
      pageSize,
      functieIds ?? [],
      inschrijfperiodeId,
      reedsIngeschreven,
      alleenOndersteuners,
    ] as const,
    queryFn: async () => {
      const { data, response } = await api.GET("/api/beheer/koppelingen/personen", {
        // The address matches only where the screen is choosing a person rather than
        // identifying one - the same split as the blank term below, and ADR-0018's reason for
        // it: on the koppeling screen the address is what the account *claims*, so a box that
        // turns it into a single hit proposes the match that design refused.
        params: {
          query: {
            q: term,
            includeEmail: toonIedereen,
            page,
            pageSize,
            // Left off entirely when nothing is ticked, so an unfiltered search is the same
            // request it was before this existed.
            ...(functieIds?.length ? { functie: functieIds } : {}),
            inschrijfperiodeId,
            reedsIngeschreven,
            alleenOndersteuners,
          },
        },
      });
      if (!data) throw new Error(i18n.t("koppelingen:errors.zoeken", { status: response.status }));
      return data;
    },
    // Two letters, unless the caller lists everybody - the ondersteuners screen does, and an
    // empty term is what asks the server for that. The koppeling screen keeps the wait: ADR-0018
    // has it proposing nobody until a beheerder has said who they are looking for.
    enabled: toonIedereen || term.trim().length >= 2,
    // Each page is its own query key, so without this a click to a page never fetched before
    // clears `data` and swaps the table for a skeleton - unmounting the very pager button just
    // clicked, which loses focus and (in some browsers) scrolls the page back to the top. The
    // previous page's rows stay on screen, correct but stale, until the new ones arrive.
    placeholderData: keepPreviousData,
  });
}

/** Addresses Portaal holds on more than one persoon. None of them can ever auto-link. */
export function useDubbeleAdressen() {
  return useQuery({
    queryKey: duplicatenKey,
    queryFn: async () => {
      const { data, response } = await api.GET("/api/beheer/koppelingen/duplicaten");
      if (!data) throw new Error(i18n.t("koppelingen:errors.duplicaten", { status: response.status }));
      return data;
    },
  });
}

export function useKoppel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, persoonId }: { userId: string; persoonId: string }) => {
      const { error, response } = await api.POST("/api/beheer/koppelingen/{userId}", {
        params: { path: { userId } },
        body: { persoonId },
      });

      if (!response.ok) {
        const problem = error as { error?: string } | undefined;
        throw new Error(
          problem?.error ?? i18n.t("koppelingen:errors.koppelen", { status: response.status }),
        );
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: lokaleAccountsKey }),
  });
}

export function useOntkoppel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (persoonId: string) => {
      const { response } = await api.DELETE("/api/beheer/koppelingen/persoon/{persoonId}", {
        params: { path: { persoonId } },
      });
      if (!response.ok) throw new Error(i18n.t("koppelingen:errors.ontkoppelen", { status: response.status }));
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: lokaleAccountsKey }),
  });
}
